/**
 * Review-of-merge items (board s1389-1 (b)): read the feed, keep a merge, take a keep back, and undo
 * the merge through the EXISTING revert lane (`revertDecision`, the one Entity Decisions uses; the
 * engine closes the item citing the merge in the same transaction). Contract:
 * `docs/specs/incoming/XC-ERP1-REVIEW-OF-MERGE-CARD_Frontend_Contract_Note_v0_1.md`.
 *
 * ER serves the feed since s1392 (`GET /reviews/merge-reviews`), so every failure (a 404 or 422, any
 * 5xx, a reply off the contract) is thrown and the Inbox says it; the tolerance that read a route not
 * served yet as nothing to review retired with the route (gate:
 * `entity_resolution_service/tests/test_routes/test_merge_reviews_not_served_tolerance.py`).
 */

import axios from 'axios';

import { config } from '@/config/environment';
import { getAuthToken } from '@/contexts/AuthContext';
import { revertDecision } from '@/services/decisionFeedService';
import {
  MergeReviewFeedSchema,
  MergeReviewSchema,
  type MergeReview,
  type MergeReviewFeed,
} from '@/types/entityReviews/mergeReviews';

export const MERGE_REVIEWS_PATH = '/api/v1/entity-reviews/merge-reviews';

/** The reason the revert lane records for an Undo pressed on this card (it rides in the rationale). */
export const UNDO_MERGE_REASON = 'Undone in the Inbox: review of a merge Wingo made';

const mergeReviewApi = axios.create({
  baseURL: config.GATEWAY_URL,
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json' },
});

mergeReviewApi.interceptors.request.use((reqConfig) => {
  const token = getAuthToken();
  if (token) {
    reqConfig.headers.Authorization = `Bearer ${token}`;
  }
  return reqConfig;
});

/** The reply did not hold to the contract: nothing of it is shown. */
export class MergeReviewContractError extends Error {
  constructor(public readonly issues: string[]) {
    super(`merge reviews failed their contract: ${issues.join('; ')}`);
    this.name = 'MergeReviewContractError';
  }
}

function issuesOf(error: { issues: { path: (string | number)[]; message: string }[] }): string[] {
  return error.issues.map((i) => `${i.path.join('.')}: ${i.message}`);
}

export async function fetchMergeReviews(page = 1, pageSize = 20): Promise<MergeReviewFeed> {
  const { data } = await mergeReviewApi.get<unknown>(MERGE_REVIEWS_PATH, {
    params: { page, page_size: pageSize },
  });
  const parsed = MergeReviewFeedSchema.safeParse(data);
  if (!parsed.success) throw new MergeReviewContractError(issuesOf(parsed.error));
  return parsed.data;
}

async function postItemAct(reviewId: string, act: 'keep' | 'undo-keep'): Promise<MergeReview> {
  const { data } = await mergeReviewApi.post<unknown>(
    `${MERGE_REVIEWS_PATH}/${encodeURIComponent(reviewId)}/${act}`,
  );
  const parsed = MergeReviewSchema.safeParse(data);
  if (!parsed.success) throw new MergeReviewContractError(issuesOf(parsed.error));
  return parsed.data;
}

/** "Keep combined": closes the item (the engine writes no verdict). */
export function keepMergeReview(reviewId: string): Promise<MergeReview> {
  return postItemAct(reviewId, 'keep');
}

/** Undo after Keep: the item is open again. */
export function undoKeepMergeReview(reviewId: string): Promise<MergeReview> {
  return postItemAct(reviewId, 'undo-keep');
}

/** "Undo the merge": the merge's existing revert lane. */
export async function undoMerge(mergeDecisionId: string): Promise<void> {
  await revertDecision(mergeDecisionId, { reason: UNDO_MERGE_REASON });
}
